import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Users,
  FileText,
  GraduationCap,
  Clock,
  CheckCircle2,
  XCircle,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import StatCard from "../../components/StatCard";
import {
  ChartCard,
  StatusDonut,
  ProgramsBar,
  TimelineChart,
} from "../../components/charts/Charts";
import {
  lastNDaysSeries,
  combineSeries,
  groupBy,
  topNFromObj,
  statusCounts,
  relativeTime,
} from "../../lib/chart-utils";
import { Link } from "react-router-dom";

export default function AdminOverview({ data, adminName, onApprove }) {
  const { stats, apps, domestic, recruiters } = data;

  const allApps = useMemo(
    () => [...(apps || []), ...(domestic || [])],
    [apps, domestic]
  );

  const appsTimeline = useMemo(
    () => lastNDaysSeries(apps, 7),
    [apps]
  );
  const domTimeline = useMemo(
    () => lastNDaysSeries(domestic, 7),
    [domestic]
  );
  const timelineData = useMemo(
    () =>
      combineSeries([
        { key: "International", data: appsTimeline },
        { key: "Domestic", data: domTimeline },
      ]),
    [appsTimeline, domTimeline]
  );

  const programData = useMemo(() => {
    const all = [
      ...(apps || []).map((a) => a.program),
      ...(domestic || []).map((a) => a.program_title),
    ].filter(Boolean);
    const grouped = groupBy(all.map((n) => ({ n })), (i) =>
      (i.n || "").split(" \u2014 ")[0]
    );
    return topNFromObj(grouped, 6);
  }, [apps, domestic]);

  const recruiterStatusData = useMemo(
    () => statusCounts(recruiters || []),
    [recruiters]
  );

  const recentActivity = useMemo(() => {
    const a = (apps || []).slice(0, 6).map((x) => ({
      type: "application",
      title: x.full_name,
      meta: x.program || "No program selected",
      time: x.created_at,
      tag: "International",
      color: "#1E3A8A",
    }));
    const d = (domestic || []).slice(0, 6).map((x) => ({
      type: "domestic",
      title: `${x.first_name || ""} ${x.last_name || ""}`.trim(),
      meta: x.program_title || "No program",
      time: x.created_at,
      tag: "Domestic",
      color: "#C49A3A",
    }));
    const r = (recruiters || []).slice(0, 6).map((x) => ({
      type: "recruiter",
      title: x.full_name,
      meta: x.company_name,
      time: x.created_at,
      tag: x.status,
      color:
        x.status === "approved"
          ? "#059669"
          : x.status === "rejected"
          ? "#DC2626"
          : "#D97706",
    }));
    return [...a, ...d, ...r]
      .sort(
        (x, y) =>
          new Date(y.time).getTime() - new Date(x.time).getTime()
      )
      .slice(0, 8);
  }, [apps, domestic, recruiters]);

  const pendingRecruiters = (recruiters || []).filter(
    (r) => r.status === "pending"
  );

  return (
    <div className="space-y-6">
      {/* Hero greeting */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#2A4FAE] text-white p-6 sm:p-8"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C49A3A]/30 blur-3xl" />
        <div className="absolute -bottom-12 -left-10 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold text-amber-300">
              <Sparkles className="w-3.5 h-3.5" /> Today ·{" "}
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">
              Welcome back, {adminName?.split(" ")[0] || "Admin"}
            </h2>
            <p className="mt-1 text-white/75 max-w-xl text-sm">
              Here’s your live admissions snapshot — applications, partners,
              and pending reviews at a glance.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-amber-200 font-semibold">
                All-time
              </div>
              <div className="font-display text-4xl font-bold">
                {allApps.length}
              </div>
              <div className="text-xs text-white/70">total applications</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          label="International"
          value={stats?.student_applications || 0}
          accent="#1E3A8A"
          spark={appsTimeline.map((d) => d.value)}
          delay={0.05}
        />
        <StatCard
          icon={GraduationCap}
          label="Domestic"
          value={stats?.domestic_applications || 0}
          accent="#C49A3A"
          spark={domTimeline.map((d) => d.value)}
          delay={0.1}
        />
        <StatCard
          icon={Users}
          label="Recruiters"
          value={stats?.recruiters_total || 0}
          accent="#059669"
          delay={0.15}
        />
        <StatCard
          icon={Clock}
          label="Pending Review"
          value={stats?.recruiters_pending || 0}
          accent="#DC2626"
          delay={0.2}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <ChartCard
            title="Applications — Last 7 days"
            subtitle="International vs Domestic"
            delay={0.25}
          >
            <TimelineChart
              data={timelineData}
              series={["International", "Domestic"]}
              colors={["#1E3A8A", "#C49A3A"]}
            />
          </ChartCard>
        </div>
        <ChartCard
          title="Recruiter Status"
          subtitle="Distribution by approval status"
          delay={0.3}
        >
          <StatusDonut data={recruiterStatusData} />
        </ChartCard>
      </div>

      {/* Programs + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <ChartCard
            title="Top Programs of Interest"
            subtitle="Across all applications"
            delay={0.35}
          >
            <ProgramsBar data={programData} />
          </ChartCard>
        </div>
        <ChartCard
          title="Recent Activity"
          subtitle="Latest updates across the portal"
          delay={0.4}
        >
          <div className="space-y-1">
            {recentActivity.length === 0 && (
              <div className="text-sm text-slate-400 py-6 text-center">
                No activity yet
              </div>
            )}
            {recentActivity.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.05, duration: 0.3 }}
                className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-slate-50"
              >
                <div
                  className="mt-1 w-2 h-2 rounded-full shrink-0"
                  style={{ background: a.color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {a.title || "Unknown"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {a.meta}
                  </div>
                </div>
                <div className="text-[11px] text-slate-400 whitespace-nowrap">
                  {relativeTime(a.time)}
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Pending Recruiters quick action */}
      {pendingRecruiters.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">
                Pending Partner Applications
              </h3>
              <p className="text-xs text-slate-500">
                Send the recruiter agreement to start onboarding
              </p>
            </div>
            <Link
              to="/admin/dashboard/recruiters"
              className="inline-flex items-center gap-1 text-xs font-semibold text-cct-blue hover:underline"
            >
              View all <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {pendingRecruiters.slice(0, 5).map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-4 p-4 hover:bg-slate-50/60"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center font-bold text-sm">
                  {r.full_name.slice(0, 1)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800">
                    {r.full_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.company_name} · {r.city}, {r.country}
                  </div>
                </div>
                <Link
                  to="/admin/dashboard/recruiters"
                  className="inline-flex items-center gap-1.5 bg-cct-blue text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-[#1a3380]"
                >
                  Send Agreement <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
